import { Settings } from "lucide-react";
import { createPortal } from "react-dom";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";

interface CookieConsentBannerProps {
  onAcceptAll: () => void;
  onEssentialOnly: () => void;
  onCustomize: () => void;
  /**
   * Short bottom bar for /refer/:code. The full card covers the partner
   * name on a phone; this row stays under the sticky project button.
   */
  compact?: boolean;
}

/**
 * Portaled to document.body so Radix Dialog's body { pointer-events: none }
 * cannot leave Accept / Essential Only looking stuck (visible but dead).
 * Inline pointer-events:auto is required for the same reason.
 */
export function CookieConsentBanner({
  onAcceptAll,
  onEssentialOnly,
  onCustomize,
  compact = false,
}: CookieConsentBannerProps) {
  if (typeof document === "undefined") return null;

  if (compact) {
    return createPortal(
      <div
        role="dialog"
        aria-modal="false"
        aria-label="Cookie consent"
        className="fixed bottom-0 left-0 right-0 z-[120] border-t border-gray-200 bg-white/95 px-3 py-2 pointer-events-auto backdrop-blur-sm dark:border-[#1e3448] dark:bg-[#0d1f2d]/95"
        data-testid="cookie-consent-banner"
        data-variant="compact"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        style={{ pointerEvents: "auto", paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs leading-snug text-gray-700 dark:text-gray-300">
            Cookies remember this visit.{" "}
            <Link
              href="/privacy"
              className="underline underline-offset-2"
              data-testid="link-banner-learn-more"
            >
              Privacy
            </Link>
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={onAcceptAll}
              className="h-8 bg-primary px-3 text-white"
              data-testid="button-accept-all-cookies"
              style={{ pointerEvents: "auto" }}
            >
              Accept
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onEssentialOnly}
              className="h-8 px-3"
              data-testid="button-essential-only-cookies"
              style={{ pointerEvents: "auto" }}
            >
              Essential only
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onCustomize}
              className="h-8 px-2 text-gray-600"
              data-testid="button-customize-cookies"
              style={{ pointerEvents: "auto" }}
            >
              <Settings className="h-4 w-4" />
              <span className="sr-only">Customize</span>
            </Button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-[120] px-4 pt-4 sm:px-6 sm:pt-6 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-[max(1.5rem,env(safe-area-inset-bottom))] pointer-events-auto"
      data-testid="cookie-consent-banner"
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      style={{ pointerEvents: "auto" }}
    >
      <div
        className="max-w-2xl mx-auto bg-white dark:bg-[#0d1f2d] border border-gray-200 dark:border-[#1e3448] rounded-2xl shadow-2xl pointer-events-auto"
        style={{ pointerEvents: "auto" }}
      >
        <div className="p-5 sm:p-6">
          <h2 className="font-heading text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-1.5">
            We value your privacy
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            We use cookies and similar technologies to improve your experience, measure
            traffic, and show relevant content. You choose what we collect beyond the
            essentials.{" "}
            <Link
              href="/privacy"
              className="underline underline-offset-2 hover:text-gray-900 dark:hover:text-white transition-colors"
              data-testid="link-banner-learn-more"
            >
              Learn more
            </Link>
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <Button
              type="button"
              onClick={onAcceptAll}
              className="flex-1 sm:flex-none touch-target bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white font-semibold shadow-sm"
              data-testid="button-accept-all-cookies"
              style={{ pointerEvents: "auto" }}
            >
              Accept All
            </Button>
            <Button
              type="button"
              onClick={onEssentialOnly}
              variant="outline"
              className="flex-1 sm:flex-none touch-target border-gray-300 dark:border-[#2a4560] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#1a2e40] font-medium"
              data-testid="button-essential-only-cookies"
              style={{ pointerEvents: "auto" }}
            >
              Essential Only
            </Button>
            <Button
              type="button"
              onClick={onCustomize}
              variant="ghost"
              className="flex-1 sm:flex-none touch-target text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white font-medium gap-1.5"
              data-testid="button-customize-cookies"
              style={{ pointerEvents: "auto" }}
            >
              <Settings className="h-4 w-4" />
              Customize
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
